import KeyboardOutlinedIcon from "@mui/icons-material/KeyboardOutlined";
import { Box, Button, Tooltip } from "@mui/material";
import React from "react";
import { useHotkeys } from "react-hotkeys-hook";

import { ConfirmDeleteModal } from "new-components/modals/ConfirmDeleteModal";
import { Typography } from "new-components/typography/Typography";

export interface DeleteImageQueryButtonProps {
  onDelete: () => void | Promise<void>;
  isDeleting?: boolean;
  hideHotkey?: boolean;
  /** When true (e.g. the Unclear feedback dialog is open), the delete hotkey is suppressed. */
  isUnclearMenuOpen?: boolean;
}

export function DeleteImageQueryButton(props: DeleteImageQueryButtonProps) {
  const { onDelete, isDeleting = false, hideHotkey = false, isUnclearMenuOpen = false } = props;

  const [openConfirmationModal, setOpenConfirmationModal] = React.useState(false);

  const handleDeleteImageQuery = async () => {
    // onDelete surfaces its own success/error feedback. Use finally so the
    // confirmation modal always closes (and never gets stuck open) regardless
    // of whether the delete succeeds or throws.
    try {
      await onDelete();
    } finally {
      setOpenConfirmationModal(false);
    }
  };

  useHotkeys(
    "delete,backspace",
    () => setOpenConfirmationModal(true),
    { enabled: !openConfirmationModal && !isUnclearMenuOpen, scopes: ["review"] },
    [openConfirmationModal, isUnclearMenuOpen]
  );

  return (
    <>
      <Tooltip title="Delete image query (Delete / Backspace)" placement="top">
        <Button
          id="delete-image-query-button"
          data-testid="delete-image-query-button"
          onClick={() => setOpenConfirmationModal(true)}
          variant="contained"
          sx={{
            height: "34px",
            flexShrink: 0,
            bgcolor: "#FFFFFF",
            borderRadius: "17px",
            textTransform: "none",
            color: "#919599",
            fontSize: "16px",
            padding: "0px 14px",
            whiteSpace: "nowrap",
            justifyContent: "center",
            boxShadow:
              "0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)",
            "& .MuiButton-endIcon": {
              margin: "0",
              marginLeft: "8px",
            },
            "&:hover": {
              bgcolor: "#FFFFFF",
              opacity: 0.8,
              boxShadow:
                "0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)",
            },
          }}
          endIcon={
            !hideHotkey && (
              <Box
                id="delete-button-end-icon"
                sx={{
                  display: { xs: "none", sm: "none", md: "flex" },
                  height: "100%",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "4px",
                  color: "#919599",
                  opacity: 0.6,
                }}
              >
                <KeyboardOutlinedIcon sx={{ height: "24px", width: "24px" }} />
                <Typography variant="detail" fontWeight="bold">
                  Del
                </Typography>
              </Box>
            )
          }
        >
          Delete
        </Button>
      </Tooltip>

      <ConfirmDeleteModal
        isDeleteModalOpen={openConfirmationModal}
        setIsDeleteModalOpen={setOpenConfirmationModal}
        modalTitle="DELETE IMAGE QUERY"
        onRemoveHandler={handleDeleteImageQuery}
        isLoadingIQDelete={isDeleting}
        modalContent="<strong>Deleting the image will mean that it will not be used to train the detector</strong><br />Are you sure you want to continue?"
      />
    </>
  );
}
